import Bugsnag from "@bugsnag/js";
import debug from "debug";
import { IOfferModeWithFSecure } from "@nju/data-access-portal-offers";
import {
  IFSecureOrchestrator,
  IFtthOrchestrator,
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
  onboardingInProgress,
} from "@nju/data-access-portal-orchestrator";
import {
  IFtthOrderProductsResponse,
  IMobileOrderProductsResponse,
} from "@nju/data-access-portal-product";

const logger = debug("resolveActiveContext");

/**
 * @see https://dop-orange.atlassian.net/wiki/spaces/DGB/pages/1739358252/ARCH+WWW+Order+Capture+MOBILE#%E2%AC%87%EF%B8%8F-FTTH-%E2%80%A6-MOBILE-%E2%9E%A1%EF%B8%8F
 */

interface IResolveActiveContextProps {
  ftth: IFtthOrchestrator | undefined;
  fsecure: IFSecureOrchestrator | undefined;
  mobile: IMobileOrchestrator | undefined;
  multiOrder: IMultiOrderOrchestrator | undefined;
  mode: IOfferModeWithFSecure | undefined;
  mobileProducts: IMobileOrderProductsResponse;
  ftthProducts: IFtthOrderProductsResponse;
}

export function resolveActiveContext({
  ftth,
  fsecure,
  mobile,
  multiOrder,
  mode,
  mobileProducts,
  ftthProducts,
}: IResolveActiveContextProps): IOfferModeWithFSecure | undefined {
  const isPostpaid =
    multiOrder?.offerCategory === "MOBILE_POSTPAID" ||
    mobile?.offerCategory === "MOBILE_POSTPAID";
  const mobileMode = isPostpaid ? "postpaid" : "mobile";

  if (!ftthProducts && !mobileProducts && !ftth && !mobile && !fsecure) {
    return undefined;
  }

  // ----------- row 1 -----
  if (
    (!ftth || ftth.state === "START") &&
    ftthProducts.hasNoProducts &&
    (!mobile || mobile.state === "START") &&
    (!multiOrder || multiOrder.state === "START") &&
    mobileProducts.hasNoProducts
  ) {
    logger("ftth and mobile are in START state");
    return undefined;
  }

  // extra logic for situation where mode is provided via `useOfferMode`
  if (mode) {
    logger("mode is set to %s", mode);
    return mode;
  }
  // end of extra logic

  // extra logic for missing orchestrators
  if (ftth && !mobile) {
    logger("ftth is defined and mobile is not");
    return "ftth";
  }

  if (!ftth && mobile && mobile.state !== "START") {
    logger("ftth is not defined and mobile is");
    return mobileMode;
  }
  // end of extra logic

  if (
    ftth?.state === "START" &&
    mobile &&
    onboardingInProgress(mobile.state, mobile.type)
  ) {
    logger("ftth is in START state and mobile is in progress state");
    return mobileMode;
  }

  if (ftth?.state === "START" && mobile?.state === "COMPLETED") {
    logger("ftth is in START state and mobile is in COMPLETED state");
    return mobileMode;
  }

  // ----------- row 2 -----
  if (
    ftth &&
    onboardingInProgress(ftth.state, ftth.type) &&
    mobile?.state === "START"
  ) {
    logger("ftth is in progress state and mobile is in START state");
    return "ftth";
  }

  if (
    ftth &&
    mobile &&
    onboardingInProgress(ftth.state, ftth.type) &&
    onboardingInProgress(mobile.state, mobile.type)
  ) {
    logger("ftth and mobile are in progress state");
    return "ftth";
  }

  if (
    ftth &&
    onboardingInProgress(ftth.state, ftth.type) &&
    mobile?.state === "COMPLETED"
  ) {
    logger("ftth is in progress state and mobile is in COMPLETED state");
    return mobileMode;
  }

  // ----------- row 3 -----
  if (ftth?.state === "COMPLETED" && mobile?.state === "START") {
    logger("ftth is in COMPLETED state and mobile is in START state");
    return "ftth";
  }

  if (
    ftth?.state === "COMPLETED" &&
    mobile &&
    onboardingInProgress(mobile.state, mobile.type)
  ) {
    logger("ftth is in COMPLETED state and mobile is in progress state");
    return "ftth";
  }

  if (ftth?.state === "COMPLETED" && mobile?.state === "COMPLETED") {
    logger("ftth is in COMPLETED state and mobile is in COMPLETED state");
    return "ftth";
  }

  const info = {
    mode: mode || "empty",
    hasFtth: !!ftth,
    hasMobile: !!mobile,
    ftthState: ftth?.state,
    mobileState: mobile?.state,
  };

  logger("Invalid state %O", info);

  Bugsnag.notify("Invalid dashboard context", (e) => {
    e.addMetadata("info", info);
  });

  return undefined;
}
